/**
 * Section score header component for QuestionsTab.
 * Displays section title, weight, score, and progress bar.
 */

import { CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Scale } from 'lucide-react';
import { QualificationSection } from '../../lib/api';
import { ResponseMap } from './types';
import { calculateSectionScore, getSectionScoreColor, getProgressBarColor } from './scoring-utils';

interface SectionScoreHeaderProps {
  section: QualificationSection;
  responses: ResponseMap;
  passScore?: number;
  /** Suppliers must not see weights or points while answering. */
  hideScores?: boolean;
}

export function SectionScoreHeader({
  section,
  responses,
  passScore = 70,
  hideScores = false,
}: SectionScoreHeaderProps) {
  const sectionScore = calculateSectionScore(section, responses);
  const hasScores = sectionScore.maxScore > 0 && !hideScores;
  const hasWeight = sectionScore.weight > 0 && !hideScores;

  return (
    <CardHeader>
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <CardTitle className="flex items-center gap-2 flex-wrap">
            {section.title}
            {hasWeight && (
              <Badge variant="outline" className="font-normal text-xs gap-1">
                <Scale className="h-3 w-3" />
                Weight: {sectionScore.weight}%
              </Badge>
            )}
          </CardTitle>
          {section.description && (
            <CardDescription className="mt-1">{section.description}</CardDescription>
          )}
        </div>

        {hasScores && (
          <div className="flex flex-col items-end gap-1 min-w-[120px]">
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">
                {sectionScore.scoreAwarded.toFixed(0)} / {sectionScore.maxScore.toFixed(0)} pts
              </span>
              <Badge
                variant="secondary"
                className={`${getSectionScoreColor(sectionScore.percentage, passScore)} font-semibold`}
              >
                {sectionScore.percentage.toFixed(0)}%
              </Badge>
            </div>
            <Progress
              value={sectionScore.percentage}
              className={`h-2 w-24 ${getProgressBarColor(sectionScore.percentage, passScore)}`}
            />
          </div>
        )}
      </div>
    </CardHeader>
  );
}
